/** Console citation atoms and labels; marker syntax is shared with the server. */
import { cleanAskCitations } from "../../../../../backend/src/shared/askCitationText";
import { citeLabel } from "../chrome";
import { citationTarget, type CitationTargetInput } from "./citationTarget";

/** One renderable piece of an answer. */
export type AskAtom =
  /** A word, keeping its trailing space. */
  | { w: string }
  /** A bold run, already stripped of its `**` markers. */
  | { b: string }
  /** A citation — the reference id (`"R1"`). */
  | { cite: string };

// One grammar is shared with the server so saved and newly generated answers agree.
import { CITE_BRACKET, citationIds } from "../../../../../backend/src/shared/askCitations";
export { CITE_BRACKET, citationIds };

/** A bold run or a citation group, retaining surrounding whitespace. */
const TOKEN = new RegExp(String.raw`\*\*(.+?)\*\*|${CITE_BRACKET}`, "g");

/** Citation brackets only — no bold alternative. Used to find citations
 *  nested inside an already-matched bold run (see `pushBoldRun`). */
const CITE_ONLY = new RegExp(CITE_BRACKET, "g");

/**
 * Parse a matched bold run's inner text for citation brackets, so
 * `**Confirmed [R1]**` yields a bold atom for "Confirmed " and a chip atom
 * for R1, instead of swallowing the bracket as literal bold text (which
 * would leak the raw `[R1]` marker on screen — the exact defect this module
 * exists to prevent).
 */
function pushBoldRun(atoms: AskAtom[], inner: string): void {
  let last = 0;
  for (const match of inner.matchAll(CITE_ONLY)) {
    if (match.index > last) atoms.push({ b: inner.slice(last, match.index) });
    for (const id of citationIds(match[1]!)) atoms.push({ cite: id });
    last = match.index + match[0].length;
  }
  if (last < inner.length) atoms.push({ b: inner.slice(last) });
}

export function buildAtoms(text: string): AskAtom[] {
  const atoms: AskAtom[] = [];

  // Split on the trailing space so a word keeps it — without this, wrapping
  // collapses the gaps between words that sit either side of a chip.
  const pushWords = (chunk: string) => {
    for (const word of chunk.split(/(?<= )/)) {
      if (word !== "") atoms.push({ w: word });
    }
  };

  // An unpaired `**` (a stray exponent like `10**2`, a truncated emphasis
  // marker) simply never completes a match here — `TOKEN`'s bold alternative
  // requires a closing `**`, so a lone marker falls through and renders as
  // literal text. There is deliberately no whole-string parity guard: an
  // earlier version counted `**` occurrences across the entire answer and
  // disabled bold parsing everywhere when the count was odd, which broke
  // legitimate, well-formed bold pairs elsewhere in the same answer just
  // because an unrelated stray `**` existed somewhere else in it (see
  // askCitations.test.ts for a worked example). A localized cosmetic glitch
  // is preferable to losing all bold formatting in an answer.
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    if (match.index > last) pushWords(text.slice(last, match.index));
    const [, bold, ids] = match;
    if (bold !== undefined) {
      pushBoldRun(atoms, bold);
    } else if (ids !== undefined) {
      for (const id of citationIds(ids)) atoms.push({ cite: id });
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) pushWords(text.slice(last));

  return atoms;
}

/** Remove unresolved and historical markers, preserving Markdown whitespace.
 * Shared with the backend so persisted answers get the same protection. */
export function stripUnknownCitations<T extends { id: string }>(
  text: string,
  references: T[],
): string {
  return cleanAskCitations(text, references.map(reference => reference.id));
}

export function citedInOrder<T extends { id: string }>(
  text: string,
  references: T[],
): T[] {
  const byId = new Map(references.map((reference) => [reference.id, reference]));
  const seen = new Set<string>();
  const cited: T[] = [];
  for (const atom of buildAtoms(text)) {
    if (!("cite" in atom)) continue;
    if (seen.has(atom.cite)) continue;
    const reference = byId.get(atom.cite);
    // An id the reference list does not contain is a model hallucination. A
    // chip for it would navigate nowhere, so it is dropped here and rendered
    // as nothing by `AnswerBody`.
    if (!reference) continue;
    seen.add(atom.cite);
    cited.push(reference);
  }
  return cited;
}

/** The distinct records shown in Sources and counted in Recent chats.
 * Multiple citation IDs can point to the same item or chunks of one walk. */
export function citedRecordsInOrder<T extends { id: string } & CitationTargetInput>(
  text: string,
  references: T[],
): T[] {
  const seen = new Set<string>();
  return citedInOrder(text, references).filter(reference => {
    const target = citationTarget(reference);
    const key = target ? `${target.target}:${target.id}` : `${reference.recordKind}:${reference.recordId || reference.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function citationLabels<T extends { id: string; recordKind: string }>(
  cited: T[],
): Record<string, string> {
  const total = new Map<string, number>();
  for (const reference of cited) {
    total.set(reference.recordKind, (total.get(reference.recordKind) ?? 0) + 1);
  }

  const running = new Map<string, number>();
  const labels: Record<string, string> = {};
  for (const reference of cited) {
    // `citeLabel` is the one fallback every citation consumer agrees on — an
    // unrecognised OR missing `recordKind` reads "Record" here exactly as it
    // does in `CiteChip` and `SourcesCard`, never the raw wire string.
    const base = citeLabel(reference.recordKind);
    if ((total.get(reference.recordKind) ?? 0) <= 1) {
      labels[reference.id] = base;
      continue;
    }
    const n = (running.get(reference.recordKind) ?? 0) + 1;
    running.set(reference.recordKind, n);
    labels[reference.id] = `${base} ${n}`;
  }
  return labels;
}
